import { useQuery } from '@tanstack/vue-query'

import { computed } from 'vue'
import { RouteLocationRaw } from 'vue-router'

import type { IconNameType } from '@/components_new/Icon'
import { useConfig } from '@/composables/config'
import { useUrl } from '@/composables/url-helpers'
import { useTr } from '@/composables/use-tr'
import { useI18nAddon } from '@/i18n/i18n'
import { ROUTE_NAMES } from '@/router/constants'
import { APIGeneralInfo, APIP2ECoinsInfo, MainCategoriesType } from '@/types'
import { shuffle } from '@/utils/helpers'
import { useAPI } from '@/utils/requests/api-plugin'
import { MAIN_CATEGORIES_CACHE } from '@/utils/requests/utils'

const PLACEHOLDER_DATA = {
    p2eBonusInfo: {} as APIP2ECoinsInfo,
    promoCategoriesList: [],
    menuHighlightCategoriesList: [],
    contentCategoriesList: [],
    menuCategoriesList: [],
}

export type ShortCategory = {
    id: MainCategoriesType
    title: string
    location: RouteLocationRaw
}

export const HIGHLIGHTED_CATEGORY_TYPES: MainCategoriesType[] = ['trending_now', 'new']

export const useGeneralData = () => {
    const tr = useTr()
    const { currentLocale } = useI18nAddon()
    const { VUE_APP_ENABLE_NEW_HOMEPAGE } = useConfig()
    const { getCategoryRoute } = useUrl()
    const { getMainCategories } = useAPI()
    const mainData = computed(() => data.value || PLACEHOLDER_DATA)
    const queryKey = computed(() => ['main-data', currentLocale.value])
    function setMainData(data: APIGeneralInfo) {
        let p2eBonusInfo: APIP2ECoinsInfo = {} as APIP2ECoinsInfo
        let promoCategoriesList: ShortCategory[] = []
        const menuHighlightCategoriesList: ShortCategory[] = []
        let contentCategoriesList: ShortCategory[] = []
        const menuCategoriesList: ShortCategory[] = []
        const { categories, p2e } = data
        p2eBonusInfo = p2e.coinsInfo
        categories.forEach(({ id, hru, title }) => {
            const mainCategoryId = id as MainCategoriesType
            if (HIGHLIGHTED_CATEGORY_TYPES.includes(mainCategoryId)) {
                menuHighlightCategoriesList.push({
                    id: mainCategoryId,
                    title,
                    location: getCategoryRoute({ hru, type: 'category' }),
                })
            } else {
                menuCategoriesList.push({
                    id: mainCategoryId,
                    title,
                    location: getCategoryRoute({ hru, type: 'category' }),
                })
            }
        })

        contentCategoriesList = [...menuHighlightCategoriesList, ...menuCategoriesList]
        promoCategoriesList = shuffle(menuCategoriesList.slice()).slice(
            0,
            Math.min(VUE_APP_ENABLE_NEW_HOMEPAGE ? 20 : 7, menuCategoriesList.length),
        )
        return {
            p2eBonusInfo,
            promoCategoriesList: VUE_APP_ENABLE_NEW_HOMEPAGE
                ? [
                      ...promoCategoriesList,
                      {
                          id: 'all_games' as IconNameType,
                          title: tr('navigation.all_games'),
                          location: { name: ROUTE_NAMES.ALL_CATEGORIES },
                      },
                  ]
                : promoCategoriesList,
            menuHighlightCategoriesList,
            contentCategoriesList,
            menuCategoriesList,
        }
    }
    const isServer = typeof window === 'undefined'
    const { data, suspense } = useQuery({
        queryKey,
        queryFn: () => getMainCategories(currentLocale.value).then(setMainData),
        staleTime: isServer ? MAIN_CATEGORIES_CACHE : Infinity,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
    })

    return {
        mainData,
        suspense,
    }
}
